import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * AuthenticateUser Page
 * 
 * Handles the SSO callback from LevelUp:
 * 1. Reads token from URL query params (ONCE - token is single-use)
 * 2. Passes token directly to LevelUp API (no decoding)
 * 3. Fetches user details from LevelUp API (NO RETRY - token is consumed)
 * 4. POSTs user data to backend /api/auth/sso
 * 5. On success, removes token from URL and redirects to /browse
 * 
 * IMPORTANT: The SSO token is SINGLE-USE. The LevelUp API marks it as used
 * on the first call. DO NOT retry or call twice.
 */
function AuthenticateUser({ onLogin }) {
  const [status, setStatus] = useState('authenticating');
  const [error, setError] = useState(null);
  const [isAccessRestricted, setIsAccessRestricted] = useState(false);
  const [isCountryMissing, setIsCountryMissing] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // CRITICAL: Prevent double processing - token is single-use!
  // This ref ensures we only process the token ONCE, even in React StrictMode
  const processedRef = useRef(false);
  const tokenRef = useRef(null);

  useEffect(() => {
    // GUARD 1: Prevent double processing in StrictMode
    if (processedRef.current) {
      console.log('SSO: Already processed, skipping duplicate call');
      return;
    }
    
    // GUARD 2: Get token ONCE and store in ref
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    // GUARD 3: If no token or already used this token, skip
    if (!token) {
      if (!tokenRef.current) {
        setError('No authentication token received');
        setStatus('error');
        toast.error('Authentication failed: No token received');
      }
      return;
    }
    
    // GUARD 4: If we already have a token stored and it's different, skip
    if (tokenRef.current && tokenRef.current !== token) {
      console.log('SSO: Different token detected, skipping');
      return;
    }
    
    // Mark as processed IMMEDIATELY before any async work
    processedRef.current = true;
    tokenRef.current = token;
    
    // Remove token from URL immediately to prevent refresh issues
    // This ensures a page refresh won't try to use the token again
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    const authenticateUser = async () => {
      try {
        setStatus('authenticating');

        // Send token directly to backend - backend will verify with LevelUp
        // The frontend does NOT call LevelUp to avoid double-consumption of single-use token
        console.log('SSO: Sending token to backend for server-side verification');

        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Minimal request - backend will get all user data from LevelUp
        // The user object is placeholder only - backend ignores it and uses LevelUp data
        const ssoRequest = {
          success: true,
          user: {
            student_user_id: 0,
            first_name: '',
            last_name: '',
            email: '',
            country: ''
          },
          sso_token: token
        };

        let ssoResponse;
        try {
          ssoResponse = await fetch(`${backendUrl}/api/auth/sso`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(ssoRequest),
          });
        } catch (fetchError) {
          console.error('Backend fetch error:', fetchError);
          throw new Error(`Cannot connect to server. Please try again later.`);
        }

        if (!ssoResponse.ok) {
          let errorMessage = `Authentication failed: ${ssoResponse.status}`;
          let isCustomBackendMessage = false;
          let isAccessRestriction = false;
          
          // For 403 errors, always try to get the detailed message
          if (ssoResponse.status === 403) {
            isAccessRestriction = true; // Assume 403 from our backend is access restriction
          }
          
          try {
            // Read response as text first
            const errorText = await ssoResponse.text();
            console.log('Raw error response:', errorText);
            
            // Try to parse as JSON
            if (errorText && errorText.trim().startsWith('{')) {
              try {
                const errorData = JSON.parse(errorText);
                if (errorData.detail) {
                  errorMessage = errorData.detail;
                  isCustomBackendMessage = true;
                  // Check for restriction markers
                  if (errorMessage.includes('COUNTRY_RESTRICTED') || 
                      errorMessage.includes('DOMAIN_RESTRICTED') ||
                      errorMessage.includes('restricted')) {
                    isAccessRestriction = true;
                  }
                }
              } catch (jsonError) {
                console.error('JSON parse error:', jsonError);
              }
            }
          } catch (parseError) {
            console.error('Error reading response:', parseError);
          }
          
          console.error('Backend SSO error:', ssoResponse.status, errorMessage, 'isAccessRestriction:', isAccessRestriction);
          
          // Create a custom error object
          const error = new Error(errorMessage);
          error.isCustomBackendMessage = isCustomBackendMessage;
          error.isAccessRestriction = isAccessRestriction;
          throw error;
        }

        const authData = await ssoResponse.json();
        console.log('SSO authentication successful:', authData.user?.email);

        // Check if admin MFA is required
        if (authData.mfa_required) {
          sessionStorage.setItem('mfa_challenge_token', authData.challenge_token);
          sessionStorage.setItem('mfa_email', authData.email || '');
          setStatus('success');
          setTimeout(() => navigate('/mfa-verify', { replace: true }), 300);
          return;
        }

        // Admin without MFA setup yet → login then redirect to setup
        if (authData.mfa_setup_required) {
          setStatus('success');
          // Set token directly in localStorage before navigation to avoid React render race
          localStorage.setItem('token', authData.token);
          window.location.replace('/mfa-setup');
          return;
        }

        setStatus('success');
        toast.success('Successfully signed in with LevelUp!');

        // Call onLogin to update app state
        if (onLogin) {
          onLogin(authData.user, authData.token);
        }

        // Redirect to the originally requested URL, fallback to /browse
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          setTimeout(() => navigate(returnUrl), 500);
        } else {
          setTimeout(() => navigate('/browse'), 500);
        }

      } catch (err) {
        console.error('SSO Authentication error:', err);
        // Extract error message - check response.data.detail first (backend error), then err.message
        const errorMessage = err.response?.data?.detail || err.message || 'Authentication failed';
        
        // SESSION_EXPIRED: Token was already used or expired
        // Show specific message telling user to re-open from SkillUp
        const sessionExpired = errorMessage === 'SESSION_EXPIRED' || 
                              errorMessage.includes('expired') ||
                              errorMessage.includes('invalid');
        
        if (sessionExpired) {
          setError('Session expired, please open Compete again from SkillUp.');
          setIsSessionExpired(true);
          setStatus('error');
          return;
        }
        
        // Check if this is an access restriction error (country or domain type)
        // IMPORTANT: Any 403 from backend SSO endpoint is an access restriction
        const isRestricted = err.isAccessRestriction || 
                            errorMessage.includes('COUNTRY_RESTRICTED') || 
                            errorMessage.includes('DOMAIN_RESTRICTED') ||
                            errorMessage.includes('BOTH_RESTRICTED') ||
                            errorMessage.includes('COUNTRY_MISSING') ||
                            errorMessage.includes('restricted in your geography') ||
                            errorMessage.includes('restricted for your partner') ||
                            errorMessage.includes('restricted') ||
                            errorMessage.includes('403'); // Fallback: any 403 is treated as access restricted
        
        // COUNTRY_MISSING is a distinct case: user hasn't set their country on LevelUp yet.
        // Show a tailored message + CTA instead of the generic "Access Restricted" copy.
        const countryMissing = errorMessage.includes('COUNTRY_MISSING');
        
        // Clean the error message (remove the type prefix if present)
        let cleanMessage = errorMessage;
        if (errorMessage.includes('|')) {
          cleanMessage = errorMessage.split('|')[1];
        }
        
        // Only use fallback if message is truly empty or just the status code
        if (isRestricted && (cleanMessage === '' || cleanMessage === '403' || cleanMessage === 'Authentication failed')) {
          // Default to the combined message only if we have no detailed message at all
          cleanMessage = 'Access to this content is currently restricted in your geography or for your partner organisation.';
        }
        
        console.log('Setting error state - isRestricted:', isRestricted, 'cleanMessage:', cleanMessage);
        
        setError(cleanMessage);
        setIsAccessRestricted(isRestricted);
        setIsCountryMissing(countryMissing);
        setStatus('error');
        
        // Don't show toast for access restricted - we'll show a nice page instead
        if (!isRestricted) {
          if (err.isCustomBackendMessage) {
            toast.error(cleanMessage);
          } else {
            toast.error(`Authentication failed: ${cleanMessage}`);
          }
        }
      }
    };

    authenticateUser();
  }, [location.search, navigate, onLogin]);

  // Render Session Expired Page
  if (status === 'error' && isSessionExpired) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#1B5E5F',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <header style={{
          backgroundColor: '#164849',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{ color: 'white', fontSize: '20px', fontWeight: 600 }}>Technofocus</span>
          <span style={{ color: '#9CA3AF' }}>|</span>
          <span style={{ color: 'white', fontSize: '16px' }}>LevelUp</span>
          <span style={{ color: '#9CA3AF', fontSize: '12px', marginLeft: '8px' }}>Sponsored by</span>
          <span style={{ color: 'white', fontSize: '14px', fontWeight: 500 }}>Microsoft</span>
        </header>

        {/* Main Content */}
        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '48px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            {/* Clock Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>

            <h1 style={{ 
              fontSize: '24px', 
              fontWeight: 600, 
              color: '#1f2937',
              marginBottom: '16px'
            }}>
              Session Expired
            </h1>

            <p style={{ 
              color: '#6b7280', 
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              Your login session has expired. Please return to SkillUp and open Compete again to continue.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <a
                href={process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  backgroundColor: '#1B5E5F',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
              >
                Go to SkillUp
              </a>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer style={{
          padding: '16px 24px',
          textAlign: 'center',
          color: 'rgba(255,255,255,0.7)',
          fontSize: '14px'
        }}>
          © 2026 Why Microsoft – Compete Enablement for Partners. Operated by Technofocus PTE LTD.
        </footer>
      </div>
    );
  }

  // Render Access Restricted Page
  if (status === 'error' && isAccessRestricted) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        backgroundColor: '#1B5E5F',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <header style={{
          backgroundColor: '#164849',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img src="/technofocus-logo.png" alt="Technofocus" style={{ height: '24px', filter: 'brightness(0) invert(1)' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>|</span>
            <img src="/levelup-logo-light.svg" alt="LevelUp" style={{ height: '24px' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>|</span>
            <img src="/ms-levelup-logo.svg" alt="Microsoft" style={{ height: '28px' }} />
          </div>
        </header>

        {/* Main Content */}
        <main style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px'
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '60px 48px',
            maxWidth: '580px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 25px 80px rgba(0,0,0,0.3)'
          }}>
            {/* Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: isCountryMissing ? '#DBEAFE' : '#FEF3C7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px'
            }}>
              {isCountryMissing ? (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              ) : (
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  <circle cx="12" cy="16" r="1"/>
                </svg>
              )}
            </div>

            {/* Title */}
            <h1 
              data-testid="auth-error-title"
              style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#1F2937', 
                marginBottom: '16px',
                lineHeight: '1.3'
              }}>
              {isCountryMissing ? 'Country Update Required' : 'Access Restricted'}
            </h1>

            {/* Message */}
            <p 
              data-testid="auth-error-message"
              style={{ 
                fontSize: '16px', 
                color: '#4B5563', 
                marginBottom: '32px',
                lineHeight: '1.6',
                maxWidth: '440px',
                margin: '0 auto 32px'
              }}>
              {error}
            </p>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: '#E5E7EB',
              margin: '32px 0'
            }} />

            {/* Support Section */}
            <div style={{
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: '12px'
              }}>
                If you believe this is an error or need assistance, please contact:
              </p>
              <a 
                href="mailto:support@skillupwithlevelup.com"
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1B5E5F',
                  textDecoration: 'none'
                }}
              >
                support@skillupwithlevelup.com
              </a>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  const levelupUrl = process.env.REACT_APP_LEVELUP_URL || 'https://skillupwithlevelup.com';
                  window.location.href = levelupUrl;
                }}
                data-testid="go-to-levelup-btn"
                style={{
                  backgroundColor: isCountryMissing ? '#1B5E5F' : '#FFFFFF',
                  color: isCountryMissing ? '#FFFFFF' : '#1B5E5F',
                  border: '2px solid #1B5E5F',
                  borderRadius: '8px',
                  padding: '14px 28px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1B5E5F';
                  e.target.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isCountryMissing ? '#1B5E5F' : '#FFFFFF';
                  e.target.style.color = isCountryMissing ? '#FFFFFF' : '#1B5E5F';
                }}
              >
                {isCountryMissing ? 'Update Country on LevelUp' : 'Go to LevelUp'}
              </button>
              <button
                onClick={() => navigate('/login')}
                data-testid="try-again-btn"
                style={{
                  backgroundColor: isCountryMissing ? '#FFFFFF' : '#1B5E5F',
                  color: isCountryMissing ? '#1B5E5F' : '#FFFFFF',
                  border: '2px solid #1B5E5F',
                  borderRadius: '8px',
                  padding: '14px 28px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = isCountryMissing ? '#1B5E5F' : '#0D4A4B';
                  e.target.style.color = '#FFFFFF';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = isCountryMissing ? '#FFFFFF' : '#1B5E5F';
                  e.target.style.color = isCountryMissing ? '#1B5E5F' : '#FFFFFF';
                }}
              >
                {isCountryMissing ? 'Retry Login' : 'Try Different Account'}
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer style={{
          backgroundColor: '#164849',
          padding: '20px 24px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
            © {new Date().getFullYear()} Why Microsoft – Compete Enablement for Partners. Operated by Technofocus PTE LTD.
          </p>
        </footer>
      </div>
    );
  }

  // Render loading/error states
  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#1B5E5F',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '12px',
        padding: '48px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {status === 'error' ? (
          <>
            {/* Error Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1F2937', 
              marginBottom: '12px' 
            }}>
              Authentication Failed
            </h2>
            <p style={{ 
              fontSize: '14px', 
              color: '#6B7280', 
              marginBottom: '24px' 
            }}>
              {error}
            </p>
            <button
              onClick={() => navigate('/login')}
              data-testid="return-to-login-btn"
              style={{
                backgroundColor: '#1B5E5F',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0D4A4B'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#1B5E5F'}
            >
              Return to Login
            </button>
          </>
        ) : (
          <>
            {/* Loading Spinner */}
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #E5E7EB',
              borderTopColor: '#1B5E5F',
              borderRadius: '50%',
              margin: '0 auto 24px',
              animation: 'spin 1s linear infinite'
            }} />
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1F2937', 
              marginBottom: '8px' 
            }}>
              {status === 'decoding' && 'Verifying credentials...'}
              {status === 'fetching' && 'Fetching your profile...'}
              {status === 'authenticating' && 'Signing you in...'}
              {status === 'success' && 'Success! Redirecting...'}
            </h2>
            <p style={{ 
              fontSize: '14px', 
              color: '#6B7280' 
            }}>
              Please wait while we authenticate you with LevelUp
            </p>
          </>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AuthenticateUser;
